import React, { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import TableBuilder, { RegisteredInputsBuilder, ApiRequest, ApiRequestBuilderV2, ToastBuilder, ButtonBuilder, ModalLinkBuilder, FormOptionsBuilder, TilesBuilder, HoverOver, ModalButtonBuilder, VerticalButtonGroup, LabelWithSubtext, CommonModal } from '../../../../../../../../GlobalComponents/Builders';
import FlexInputBuilder from '../../../../../../../../GlobalComponents/Builders/FlexInputBuilder';
import { Row, Col, CardHeader, CardBody, CardFooter, Form, Card, Container, Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap'
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom';
import { H4 } from '../../../../../../../../AbstractElements';
import { buildOptionsFromArray, setInitialFormValues, showRelativeTime, showTimestamp, showTimestampStacked, useIsMobile, tabTag, jsonPre, handleCopy, sortByKey, usePageParams } from '../../../../../../../../GlobalComponents/Helpers';
import { ContentLoader } from '../../../../../../../../Layout/Loader';
import CountUp from 'react-countup';
import { AccountContext } from '../../../../../../../../GlobalComponents/Authentication/Accounts';
import { Copy, Plus, Trash2 } from 'react-feather';
import Select from 'react-select';
import ActivityLogViewer from '../shared/ActivityLogViewer';
import RecordStateCacheViewer from './RecordStateCacheViewer';
import IsolatedLogViewer from './IsolatedLogViewer';
import JsonViewer from './JsonViewer';
import DMTRecordManage from './DMTRecordManage';
import DMTJsonFieldSearch from './DMTJsonFieldSearch';

const DMTProgressDashboard = ({ jobData, showDetails = true, showTiles = true, showLoader = true }) => {

    document.title = 'Progress Dashboard'

    const { jobIdParam } = useParams();
    const [searchParams] = useSearchParams();
    const jobId = jobData.job_id;

    const { register, handleSubmit, setValue, reset, control, getValues, formState: { errors } } = useForm({
        defaultValues: { job_resource_id: [], record_status: [], record_id: '', sort_order: 'updated_at DESC', dryRun: 'true' }
    });

    const navigate = useNavigate();

    const isMobile = useIsMobile();

    const { portalEmulation, isAdmin } = useContext(AccountContext);
    const [loadingPage, setLoadingPage] = useState(false);
    const [isProcessing, setIsProcessing] = useState(true);
    const [allRecords, setAllRecords] = useState([]);
    const [displayRecords, setDisplayRecords] = useState([]);
    const [jobResources, setJobResources] = useState([]);
    const [renderResults, setRenderResults] = useState(false);
    const [completeCount, setCompleteCount] = useState(0);
    const [totalCount, setTotalCount] = useState(null);
    const [progressData, setProgressData] = useState(null);
    const [latestTimestamp, setLatestTimestamp] = useState(null);
    const [BasicTab, setBasicTab] = useState('0');
    const [showDisclaimer, setShowDisclaimer] = useState(false);
    const [availableStatuses, setAvailableStatuses] = useState([]);
    const [availableActions, setAvailableActions] = useState([]);
    const [settingsLoaded, setSettingsLoaded] = useState(false);
    const [showMultiSearch, setShowMultiSearch] = useState(false);
    const [multiSearchRows, setMultiSearchRows] = useState([{ resource: '', recordIds: '', idType: 'both' }]);
    const [isMultiSearchResult, setIsMultiSearchResult] = useState(false);

    // JSON Field Search (REQ-365) — safe JEXTRACT/column filter builder
    const emptyJsonFilter = () => ({ column: 'resource_data', path: '', op: '=', value: '' });
    const [showJsonSearch, setShowJsonSearch] = useState(false);
    const [jsonFilterRows, setJsonFilterRows] = useState([emptyJsonFilter()]);
    const [jsonMatch, setJsonMatch] = useState('and');           // 'and' | 'or'
    const [jsonSearchResource, setJsonSearchResource] = useState(''); // '' = all resources
    const [jsonSearchMode, setJsonSearchMode] = useState('list');  // 'list' | 'count'
    const [jsonGroupBy, setJsonGroupBy] = useState({ column: '', path: '' });
    const [jsonCountResult, setJsonCountResult] = useState(null);  // count-mode result object
    const [isJsonSearchResult, setIsJsonSearchResult] = useState(false);

    const services = {
        'connectwise-cloud': 'ConnectWise',
        'connectwise-onprem': 'ConnectWise Manage - SQL',
        'servicenow': 'ServiceNow',
        'salesforce': 'SalesForce',
        'simplesat': 'SimpleSat',
        'csv': 'CSV',
        'autotask': 'Autotask',
        'dynamics': 'Dynamics365',
        'mavenlink': 'Mavenlink',
        'netsuite': 'NetSuite',
        'jira': 'Jira',
        'pandadoc': 'PandaDoc',
        'halo': 'Halo',
        'servicenow-ntt': 'SN - NTT',
        'shippo': 'Shippo',
        'freshservice': 'FreshService'
    }

    const getServiceName = (serviceCode) => services[serviceCode] || serviceCode || '';

    const jobTypeLabels = {
        'migration': 'Migration',
        'setup-tables': 'Setup Tables',
        'file-import': 'File Import',
        'one-time-update': 'One-Time Update',
        'clean-up': 'Clean Up',
    }

    const getJobTypeLabel = (job) => {
        if (job.is_sync) return 'Sync';
        return jobTypeLabels[job.job_type] || 'Migration';
    }

    const tiles = [
        {
            id: 1,
            icon: 'FilePlus',
            title: `Records ${jobData && jobData.is_sync ? 'Synced' : 'Migrated'}`,
            count: <CountUp end={completeCount} duration={3} />,
            backgroundIcon: 'CheckCircle',
        },
        {
            id: 2,
            icon: 'File',
            title: 'Errors',
            count: <CountUp end={progressData && totalCount ? progressData.reduce((a, b) => a + b.error, 0) : 0} duration={2} />,
            backgroundIcon: 'AlertTriangle',
        },
        {
            id: 3,
            icon: 'Activity',
            title: 'Progress',
            content: totalCount ? <ModalLinkBuilder
                title={`${jobData && jobData.is_sync ? 'Sync' : 'Migration'} Progress`}
                size={jobData && jobData.is_sync && progressData && progressData.length <= 3 ? 'lg' : 'xl'}
                customLabel={<span className='tile-counter'>{(completeCount * 100 / totalCount).toFixed(0) + '%'}</span>}
                onSubmit={false}
                onCancelLabel='Close'
                body={
                    jobData && jobData.is_sync && progressData && progressData.length <= 3 ? (
                        <Fragment>
                            {progressData.map((r, idx) => {
                                const doneCount = r.complete + r.cancelled;
                                const pct = r.total > 0 ? (doneCount / r.total * 100).toFixed(1) : 0;
                                const stats = [
                                    { label: 'Synced', value: r.complete, color: '#198754' },
                                    { label: 'Error', value: r.error, color: '#dc3545' },
                                    { label: 'Cancelled', value: r.cancelled, color: '#ffc107' },
                                    { label: 'Staged', value: r.staged, color: '#6c757d' },
                                    { label: 'Queued', value: r.queued, color: '#0d6efd' },
                                    { label: 'DryRun', value: r.dryRun, color: '#6c757d' },
                                    { label: 'Adopted', value: r.adopt, color: '#6c757d' },
                                    { label: 'Other', value: r.other, color: '#6c757d' },
                                ].filter(s => s.value > 0);
                                return (
                                    <div key={idx} style={{ padding: '12px 0', borderBottom: idx < progressData.length - 1 ? '1px solid #e9ecef' : 'none', marginBottom: idx < progressData.length - 1 ? 12 : 0 }}>
                                        <div className='d-flex justify-content-between align-items-baseline' style={{ marginBottom: 8 }}>
                                            <h5 style={{ margin: 0 }}>{r.name}</h5>
                                            <span style={{ fontSize: 20, fontWeight: 700 }}>{pct}%</span>
                                        </div>
                                        <div style={{ height: 8, borderRadius: 4, background: '#e9ecef', overflow: 'hidden', marginBottom: 12 }}>
                                            <div style={{ height: '100%', display: 'flex' }}>
                                                <div style={{ width: `${r.total > 0 ? (r.complete / r.total * 100) : 0}%`, background: '#198754', transition: 'width 0.4s' }} />
                                                <div style={{ width: `${r.total > 0 ? (r.cancelled / r.total * 100) : 0}%`, background: '#ffc107', transition: 'width 0.4s' }} />
                                                <div style={{ width: `${r.total > 0 ? (r.error / r.total * 100) : 0}%`, background: '#dc3545', transition: 'width 0.4s' }} />
                                            </div>
                                        </div>
                                        <div className='d-flex flex-wrap align-items-center' style={{ gap: '6px 14px', fontSize: 13 }}>
                                            {stats.map((s, i) => (
                                                <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.color, display: 'inline-block' }} />
                                                    <span style={{ color: '#555' }}>{s.label}</span>
                                                    <span style={{ fontWeight: 600 }}>{s.value.toLocaleString()}</span>
                                                </span>
                                            ))}
                                        </div>
                                        <div className='d-flex justify-content-between align-items-center' style={{ fontSize: 13, marginTop: 12, color: '#999' }}>
                                            <span>Total: <strong style={{ color: '#333' }}>{r.total.toLocaleString()}</strong></span>
                                        </div>
                                    </div>
                                );
                            })}
                            {latestTimestamp && <div style={{ fontSize: 12, color: '#aaa', marginTop: 8, textAlign: 'right' }}>
                                Last activity: {showRelativeTime(latestTimestamp)}
                            </div>}
                        </Fragment>
                    ) : (
                        <Fragment>
                            <TableBuilder
                                data={progressData}
                                processing={progressData ? false : true}
                                pagination={false}
                                columns={[
                                    {
                                        name: <p style={{ width: '750px', textAlign: 'center' }}> Resource</p>,
                                        selector: (row) => row.name,
                                        sortable: true,
                                        center: true,
                                    },
                                    {
                                        name: 'Progress',
                                        selector: (row) => <span style={{ fontWeight: 600 }}>{((row.complete + row.cancelled) / row.total * 100).toFixed(0)}%</span>,
                                        sortable: true,
                                        center: true,
                                    },
                                    {
                                        name: 'Complete',
                                        selector: (row) => row.complete,
                                        sortable: true,
                                        center: true,
                                    },
                                    {
                                        name: 'Cancelled',
                                        selector: (row) => row.cancelled > 0 ? <span style={{ color: '#ffc107' }}>{row.cancelled}</span> : 0,
                                        sortable: true,
                                        center: true,
                                    },
                                    {
                                        name: 'Staged',
                                        selector: (row) => row.staged,
                                        sortable: true,
                                        center: true,
                                    },
                                    {
                                        name: 'Queued',
                                        selector: (row) => row.queued,
                                        sortable: true,
                                        center: true,
                                    },
                                    {
                                        name: 'DryRun',
                                        selector: (row) => row.dryRun,
                                        sortable: true,
                                        center: true,
                                    },
                                    {
                                        name: 'Error',
                                        selector: (row) => <span style={{ color: 'red' }}>{row.error}</span>,
                                        sortable: true,
                                        center: true,
                                    },
                                    {
                                        name: 'Adopted',
                                        selector: (row) => row.adopt,
                                        sortable: true,
                                        center: true,
                                    },
                                    {
                                        name: 'Other',
                                        selector: (row) => row.other,
                                        sortable: true,
                                        center: true,
                                    },
                                ]}
                            />
                        </Fragment>
                    )
                }
            /> : '—',
            backgroundIcon: 'Activity'
        },
        {
            id: 4,
            icon: 'Clock',
            title: 'Last Ran',
            content: latestTimestamp ? showRelativeTime(latestTimestamp) : '—',
        }
    ]

    const getTileData = () => {

        ApiRequest('GET', '/dmt/jobs/{jobId}/progress', { pathParams: { jobId }, setIsProcessing }).then(function (results) {
            if (results.data && results.data.length > 0) {

                showTiles ? setProgressData(formatProgressData(results.data)) : null

                // the sum of all resolved status records
                const resolvedStatuses = new Set(availableStatuses.filter(s => s.category === 'resolved' || s.category === 'cancelled').map(s => s.name));
                showTiles ? setCompleteCount(results.data.reduce((a, b) => a + (resolvedStatuses.has(b.status) ? b.record_count : 0), 0)) : null

                // the sum of all records
                const count = results.data.reduce((a, b) => a + b.record_count, 0)
                showTiles ? setTotalCount(count) : null

                // the latest of all last_updated
                const lastTimestamp = results.data.reduce((a, b) => a > b.last_updated ? a : b.last_updated, 0)
                showTiles ? setLatestTimestamp(lastTimestamp * 1000) : null

                const tabs = {}
                tabs[0] = { title: 'none', resource: 'All Resources', count: -1, filter: 'All', value: 0 }

                for (let i = 0; i < results.data.length; i++) {
                    let resource = results.data[i];
                    if (tabs[resource.job_resource_id] === undefined) {
                        tabs[resource.job_resource_id] = { title: resource.name, resource_id: resource.resource_id, count: resource.record_count, filter: resource.job_resource_id, resource: `${resource.resource_id} - ${resource.name}`, fetch_count: 0 }
                    } else {
                        tabs[resource.job_resource_id].count += resource.record_count
                    }
                }

                const resources = Object.values(tabs)
                setJobResources(resources)
                setJobResources(tabs)

                if (count > 1000) {
                    setShowDisclaimer(true)
                }

                refreshRecordData()


            } else {
                clear()
            }
        })
        return true;
    }

    const clear = () => {
        setProgressData(null)
        setJobResources([])
    }

    const refreshRecordData = (toast = false, callback = null) => {
        setIsMultiSearchResult(false);
        setIsJsonSearchResult(false);
        setJsonCountResult(null);
        setRenderResults(false)
        ApiRequest('GET', '/dmt/jobs/{jobId}/records', { pathParams: { jobId } }).then(function (results) {
            if (results.data) {

                const countMap = results.data.reduce((acc, item) => {
                    const id = item.job_resource_id;
                    acc[id] = (acc[id] || 0) + 1;
                    return acc;
                }, {});

                setJobResources(prev => {
                    const updated = { ...prev };

                    Object.entries(updated).forEach(([key, value]) => {
                        value.fetch_count = key in countMap ? countMap[key] : 0
                    });

                    return updated;
                });


                results.data = handleRecords(results.data)
                setAllRecords(results.data)
                setDisplayRecords(results.data)
                setRenderResults(true)

                setBasicTab('0')

                toast ? ToastBuilder('success', `Found ${results.data.length} Records`) : null
                callback ? callback() : null
            }
        })
    }

    const formatProgressData = (data) => {

        const resolvedStatuses = new Set(availableStatuses.filter(s => s.category === 'resolved').map(s => s.name));
        const cancelledStatuses = new Set(availableStatuses.filter(s => s.category === 'cancelled').map(s => s.name));
        // Errors tile counts 'error' and 'failed' only. The error category also
        // contains 'incomplete', which is a different concept and must NOT be counted
        // here (it falls through to 'Other').
        const errorStatuses = new Set(['error', 'failed']);
        const knownStatuses = new Set(['staged', 'complete', 'error', 'queued', 'dryRun', 'adopt']);

        /* each resource is an entry with staged, complete, error, queued and total props */
        let resources = {}
        for (let i = 0; i < data.length; i++) {
            let record = data[i]
            if (resources[record.resource_id] === undefined) {
                resources[record.resource_id] = {
                    name: record.name,
                    staged: 0,
                    dryRun: 0,
                    complete: 0,
                    cancelled: 0,
                    error: 0,
                    adopt: 0,
                    queued: 0,
                    other: 0,
                    total: 0
                }
            }
            resources[record.resource_id].total += record.record_count

            if (resolvedStatuses.has(record.status)) {
                resources[record.resource_id].complete += record.record_count
            } else if (cancelledStatuses.has(record.status)) {
                resources[record.resource_id].cancelled += record.record_count
            } else if (errorStatuses.has(record.status)) {
                resources[record.resource_id].error += record.record_count
            } else {
                switch (record.status) {
                    case 'staged':
                        resources[record.resource_id].staged += record.record_count
                        break;
                    case 'error':
                        resources[record.resource_id].error += record.record_count
                        break;
                    case 'queued':
                        resources[record.resource_id].queued += record.record_count
                        break;
                    case 'dryRun':
                        resources[record.resource_id].dryRun += record.record_count
                        break;
                    case 'adopt':
                        resources[record.resource_id].adopt += record.record_count
                        break;
                    default:
                        if (!knownStatuses.has(record.status)) {
                            resources[record.resource_id].other += record.record_count
                        }
                        break;
                }
            }
        }
        return Object.values(resources)
    }

    const renderJobDetails = () => {
        if (!jobData) return null;
        const arrow = <span className='txt-secondary' style={{ margin: '0 6px', fontSize: 11 }}>→</span>;
        const connectionLink = (connection) => isAdmin
            ? <Link to={`${process.env.PUBLIC_URL}/connections/${connection.guid}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>{connection.name}</Link>
            : connection.name;
        return (
            <div className='d-flex align-items-center justify-content-between flex-wrap gap-3' style={{ fontSize: 13 }}>
                <div className='d-flex align-items-center gap-4 flex-wrap'>
                    <div style={{ minWidth: 250, maxWidth: 350 }}>
                        <span style={{ fontWeight: 600 }}>{jobData.name}</span>
                        {isAdmin && <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                            {getJobTypeLabel(jobData)} — #{jobData.id}{jobData.sync_job_id ? <>{arrow}{jobData.sync_job_id}</> : ''}
                        </div>}
                    </div>
                    <div style={{ borderLeft: '1px solid #e0e0e0', paddingLeft: 45 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'auto auto auto', gap: '0 0', alignItems: 'baseline' }}>
                            <div style={{ textAlign: 'right' }}>{connectionLink(jobData.source_connection)}</div>
                            <div style={{ textAlign: 'center', padding: '0 8px' }}>{arrow}</div>
                            <div style={{ color: jobData.is_production ? 'red' : '' }}>{connectionLink(jobData.destination_connection)}</div>
                            <div style={{ textAlign: 'right', fontSize: 11 }} className='txt-secondary'>{getServiceName(jobData.source_connection.service_code)}</div>
                            <div></div>
                            <div style={{ fontSize: 11 }} className='txt-secondary'>{getServiceName(jobData.destination_connection.service_code)}</div>
                        </div>
                    </div>
                    {jobData.test_connection.name && (
                        <div style={{ borderLeft: '1px solid #e0e0e0', paddingLeft: 16 }}>
                            <div style={{ fontSize: 11, color: '#999' }}>Test</div>
                            <div style={{ color: jobData.is_production ? '' : 'green' }}>{jobData.test_connection.name}</div>
                        </div>
                    )}
                    {totalCount ? (
                        <div style={{ borderLeft: '1px solid #e0e0e0', paddingLeft: 16 }}>
                            <div style={{ fontSize: 11, color: '#999' }}>Records</div>
                            <div style={{ fontWeight: 600 }}>{totalCount.toLocaleString()}</div>
                        </div>
                    ) : null}
                </div>
                <div className='d-flex gap-2'>
                    <ButtonBuilder label='Refresh' className='btn-secondary' style={{ fontSize: 12 }}
                        onClick={() => { getTileData(); ToastBuilder('success', 'Refreshing...') }} useSpinner={false} />
                    <ButtonBuilder label='Back to Job' className='btn-primary' style={{ fontSize: 12 }}
                        onClick={() => navigate(`${process.env.PUBLIC_URL}/tools/${jobData.route}/jobs/${jobId}`)} useSpinner={false} />
                    {jobData.sync_job_id ? <ButtonBuilder label='Dest. Progress' className='btn-primary' style={{ fontSize: 12 }} onClick={() => { window.open(`${process.env.PUBLIC_URL}/tools/${jobData.route}/jobs/${jobData.sync_job_id}/progress`) }} /> : null}
                </div>
            </div>
        )
    }

    const handleRecords = (data) => {

        // Attach a searchable string for the displayed Updated timestamp so users can
        // filter by the human-readable date/time (e.g. "6/19/2026" or "10:03") even
        // though updated_at is stored as a unix number. Covers both the desktop
        // (toLocaleString) and mobile (date + time) renderings.
        if (Array.isArray(data)) {
            for (let i = 0; i < data.length; i++) {
                if (data[i] && data[i].updated_at) {
                    const d = new Date(data[i].updated_at * 1000);
                    data[i].updated_at_display = `${d.toLocaleString()} ${d.toLocaleDateString()} ${d.toLocaleTimeString()}`;
                }
            }
        }

        if (jobData.source_connection.service_code !== 'servicenow' ) return data

        for (let i = 0; i < data.length; i++) {
            let id = data[i].source_rec_id;
            let number = data[i]?.source_raw_id ? data[i]?.source_raw_id : data[i]?.resource_data && data[i]?.resource_data.number ? data[i].resource_data.number : id;
            let rec_identifier = data[i]?.source_rec_identifier ? data[i]?.source_rec_identifier : data[i]?.resource_data && data[i]?.resource_data.sys_id ? data[i].resource_data.sys_id : id;

            if (jobData.source_connection.service_code === 'servicenow') {
                data[i].resource_data = { id, number, rec_identifier, ...data[i].resource_data }
            }

            if (data[i].resource_data && !isNaN(id)) {
                data[i].resource_data.id = parseInt(id)
            }
            try {
                if (data[i].error_message && data[i].error_message.error && typeof data[i].error_message.error.message !== 'object') {
                    data[i].error_message.error.message = JSON.parse(data[i].error_message.error.message)
                }
            } catch (err) {
                console.warn('rec handler', err.message)
            }
        }
        return data
    }


    /**
     * Normalize a raw ID list string into "n1,n2,n3" where each n is an integer.
     * - Accepts mixed separators (commas, spaces, newlines, tabs).
     * - Extracts digit groups from non-numeric tokens (e.g., "ABC00123" -> "123").
     * - Removes leading zeros via Number(), preserves order.
     * - Returns "" if no numeric content found.
     */
    const normalizeIdList = (raw) => {
        if (raw == null) return "";
        const str = String(raw);

        // Replace any non-digit with a space to isolate digit groups,
        // then split on whitespace and filter empties.
        const groups = str
            .replace(/[^\d]+/g, " ")
            .trim()
            .split(/\s+/)
            .filter(Boolean);

        // Normalize numbers (remove leading zeros by casting to Number),
        // then back to string. This preserves numeric intent.
        const normalized = groups.map(g => String(Number(g)));

        // If no numeric groups found, return empty string.
        return normalized.length ? normalized.join(",") : "";
    }

    // Record-ID textareas: Enter submits the search; Shift+Enter inserts a newline.
    const submitOnEnter = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            e.currentTarget.form?.requestSubmit();
        }
    };

    const onMultiResourceSearch = async (silent = false) => {
        setRenderResults(false);
        setIsMultiSearchResult(true);
        setIsJsonSearchResult(false);
        setJsonCountResult(null);

        const validRows = multiSearchRows.filter(r => r.resource && r.resource !== '');
        if (validRows.length === 0) {
            refreshRecordData(false, silent ? null : () => ToastBuilder('success', 'Fetching All Records'));
            return;
        }

        const formValues = getValues();

        // Build the multi_search payload: [{resource, recordIds}, ...]
        const multiSearch = validRows.map(row => ({
            resource: row.resource,
            recordIds: row.recordIds.trim() ? normalizeIdList(row.recordIds) : '',
            idType: row.idType || 'both'
        }));

        const queryParams = {
            multi_search: JSON.stringify(multiSearch),
        };
        if (formValues.sort_order) {
            queryParams.sort_order = formValues.sort_order;
        }

        try {
            const results = await ApiRequest('GET', '/dmt/jobs/{jobId}/records', { pathParams: { jobId }, queryParams });
            const data = results.data || [];

            if (data.length > 0) {
                const countMap = data.reduce((acc, item) => {
                    const id = item.job_resource_id;
                    acc[id] = (acc[id] || 0) + 1;
                    return acc;
                }, {});

                Object.entries(jobResources).forEach(([key, value]) => {
                    value.fetch_count = key in countMap ? countMap[key] : 0;
                });

                const processed = handleRecords(data);
                setDisplayRecords(processed);
                setAllRecords(processed);
                setRenderResults(true);
                setBasicTab('0');
                if (!silent) ToastBuilder('success', `Found ${data.length} Records`);
            } else {
                setAllRecords([]);
                setDisplayRecords([]);
                setRenderResults(true);
                if (!silent) ToastBuilder('info', 'No Records Found');
            }
        } catch (err) {
            console.log(err);
            ToastBuilder('error', 'Unable to Search Records');
            setRenderResults(true);
        }
    };

    // ── JSON Field Search (REQ-365) ─────────────────────────────────────────
    // Column options. JSON-capable columns expose a JEXTRACT path input.
    const JSON_SEARCH_COLUMNS = [
        { label: 'Resource Data (JSON)', value: 'resource_data', json: true },
        { label: 'Response Data (JSON)', value: 'response_data', json: true, migrationOnly: true },
        { label: 'Error Message', value: 'error_message', json: true },
        { label: 'Status', value: 'status', json: false },
        { label: 'Action', value: 'action', json: false },
    ];

    // Operators. `noValue` ops (null/empty checks) hide the value input; `listValue`
    // ops accept a comma-separated list.
    const JSON_SEARCH_OPERATORS = [
        { label: '=', value: '=' },
        { label: '!=', value: '!=' },
        { label: 'LIKE', value: 'like' },
        { label: 'NOT LIKE', value: 'not like' },
        { label: 'contains', value: 'contains' },
        { label: 'REGEXP', value: 'regexp' },
        { label: 'IN', value: 'in', listValue: true },
        { label: 'NOT IN', value: 'not in', listValue: true },
        { label: 'IS NULL', value: 'is null', noValue: true },
        { label: 'IS NOT NULL', value: 'is not null', noValue: true },
        { label: 'IS EMPTY', value: 'is empty', noValue: true },
    ];

    const columnIsJson = (col) => JSON_SEARCH_COLUMNS.find(c => c.value === col)?.json;
    const opIsNoValue = (op) => JSON_SEARCH_OPERATORS.find(o => o.value === op)?.noValue;
    const opIsList = (op) => JSON_SEARCH_OPERATORS.find(o => o.value === op)?.listValue;

    // Filter available columns by job type (response_data is migration-only).
    const jsonColumnOptions = useMemo(
        () => JSON_SEARCH_COLUMNS.filter(c => !(c.migrationOnly && jobData && jobData.is_sync)).map(c => ({ label: c.label, value: c.value })),
        [jobData && jobData.is_sync]
    );

    // Translate a UI filter row into the API filter shape, or null if incomplete.
    // Handles three shapes: element-match, array-any, and scalar/JEXTRACT leaf.
    const buildApiFilter = (row) => {
        if (!row.column) return null;

        // --- Element-match: {column, elementMatch:{arrayPath, conditions:[{key,op,value}]}} ---
        if (row.elementMatch) {
            const arrayPath = (row.elementMatch.arrayPath || '').trim();
            if (!arrayPath) return null;
            const conditions = (row.elementMatch.conditions || [])
                .map(c => {
                    const key = (c.key || '').trim();
                    if (!key) return null;
                    const cond = { key, op: c.op };
                    if (opIsNoValue(c.op)) return cond;              // null/empty checks need no value
                    if (c.value === '' || c.value == null) return null;
                    cond.value = c.value;
                    return cond;
                })
                .filter(Boolean);
            if (conditions.length === 0) return null;
            return { column: row.column, elementMatch: { arrayPath, conditions } };
        }

        const filter = { column: row.column, op: row.op };

        // --- Array-any: {column, path:'arrayKey/leafKey', arrayAny:true, op, value} ---
        if (row.arrayAny && columnIsJson(row.column)) {
            const path = (row.path || '').trim();
            if (!path || path.split('/').length !== 2) return null;  // needs arrayKey/leafKey
            filter.path = path;
            filter.arrayAny = true;
            if (row.value === '' || row.value == null) return null;  // arrayAny ops all need a value
            filter.value = row.value;
            return filter;
        }

        // --- Scalar / JEXTRACT leaf ---
        if (columnIsJson(row.column) && row.path && row.path.trim() !== '') {
            filter.path = row.path.trim();
        }
        if (opIsNoValue(row.op)) return filter;                 // null/empty checks
        if (opIsList(row.op)) {
            const list = String(row.value || '').split(',').map(v => v.trim()).filter(Boolean);
            if (list.length === 0) return null;
            filter.value = list;
            return filter;
        }
        if (row.value === '' || row.value == null) return null; // needs a value
        filter.value = row.value;
        return filter;
    };

    const onJsonFieldSearch = async (silent = false) => {
        const filters = jsonFilterRows.map(buildApiFilter).filter(Boolean);
        const isCount = jsonSearchMode === 'count';

        // Count mode may run with no filters (e.g. "how many in status X" via groupBy);
        // list mode with no filters just lists (scoped by resource) — allowed.
        const body = {
            mode: isCount ? 'count' : 'list',
            match: jsonMatch,
            filters,
        };
        if (jsonSearchResource && jsonSearchResource !== '') {
            body.resourceId = parseInt(jsonSearchResource);
        }
        if (isCount && jsonGroupBy.column) {
            body.groupBy = { column: jsonGroupBy.column };
            if (columnIsJson(jsonGroupBy.column) && jsonGroupBy.path && jsonGroupBy.path.trim() !== '') {
                body.groupBy.path = jsonGroupBy.path.trim();
            }
        }
        if (!isCount) {
            body.pageSize = 200;
            body.page = 1;
        }

        setRenderResults(false);
        const res = await ApiRequestBuilderV2('dmtJobsJobIdRecordsSearchPost', {
            pathParams: { jobId },
            body,
        });

        if (!res.success || (res.data && res.data.success === false)) {
            const message = (res.data && res.data.message) || res.error || 'Search failed';
            ToastBuilder('error', message);
            setRenderResults(true);
            return;
        }

        const payload = res.data || {};

        if (isCount) {
            setJsonCountResult(payload);
            setIsJsonSearchResult(true);
            setIsMultiSearchResult(false);
            setRenderResults(true);
            if (!silent) ToastBuilder('success', `Count: ${payload.total ?? 0}`);
            return;
        }

        // List mode — feed the flat results table (same shape as multi-search).
        const data = handleRecords(payload.records || []);
        setJsonCountResult(null);
        setIsJsonSearchResult(true);
        setIsMultiSearchResult(false);
        setDisplayRecords(data);
        setAllRecords(data);
        setRenderResults(true);
        setBasicTab('0');
        if (!silent) {
            data.length > 0
                ? ToastBuilder('success', `Found ${data.length}${payload.total > data.length ? ` of ${payload.total}` : ''} Records`)
                : ToastBuilder('info', 'No Records Found');
        }
    };

    const onRecordSearch = (searchQuery = {}, silent = false) => {

        // If JSON field search mode is active with any usable filter, delegate to it.
        if (showJsonSearch && (jsonSearchMode === 'count' || jsonFilterRows.some(r => buildApiFilter(r)))) {
            onJsonFieldSearch(silent);
            return;
        }

        // If multi-search mode is active, delegate to the multi-resource handler
        if (showMultiSearch && multiSearchRows.some(r => r.resource && r.recordIds.trim())) {
            onMultiResourceSearch(silent);
            return;
        }

        setIsJsonSearchResult(false);
        setJsonCountResult(null);

        setIsMultiSearchResult(false);
        setRenderResults(false)

        // Resources / statuses are multiselects — empty array means "all".
        const resourceIds = Array.isArray(searchQuery.job_resource_id) && searchQuery.job_resource_id.length > 0
            ? searchQuery.job_resource_id.map(id => parseInt(id))
            : null
        const statuses = Array.isArray(searchQuery.record_status) && searchQuery.record_status.length > 0
            ? searchQuery.record_status
            : null

        const hasResource = !!resourceIds
        const hasStatus = !!statuses
        const hasRecordId = searchQuery.record_id && searchQuery.record_id !== ''

        if (!hasResource && !hasStatus && !hasRecordId) {
            refreshRecordData(false, silent ? null : () => ToastBuilder('success', 'Fetching All Records'))
            return
        }

        let queryParams = {};

        if (resourceIds) {
            queryParams.job_resource_ids = resourceIds
        } else {
            queryParams.job_resource_ids = Object.values(jobResources).filter(resource => resource.filter !== 'All').map(resource => resource.filter)
        }

        if (hasRecordId) {
            let record_id = normalizeIdList(searchQuery.record_id);
            queryParams.record_id = record_id
        }

        if (statuses) {
            queryParams.record_status = statuses.join(',')
        }

        if (searchQuery.sort_order && searchQuery.sort_order !== '') {
            queryParams.sort_order = searchQuery.sort_order
        }

        ApiRequest('GET', '/dmt/jobs/{jobId}/records', { pathParams: { jobId }, queryParams: queryParams }).then(function (results) {
            if (results.data && results.data.length > 0) {

                const countMap = results.data.reduce((acc, item) => {
                    const id = item.job_resource_id;
                    acc[id] = (acc[id] || 0) + 1;
                    return acc;
                }, {});

                // Update fetch_count in place — no state update, avoids form dropdown reset
                Object.entries(jobResources).forEach(([key, value]) => {
                    value.fetch_count = key in countMap ? countMap[key] : 0
                });

                results.data = handleRecords(results.data)

                setDisplayRecords(results.data)
                setAllRecords(results.data)
                setRenderResults(true)

                setBasicTab('0')

                if (!silent) ToastBuilder('success', `Found ${results.data.length} Records`)
            } else {
                setAllRecords([])
                setDisplayRecords([])
                setRenderResults(true)
                if (!silent) ToastBuilder('info', 'No Records Found')
            }
        }).catch((err) => {
            console.log(err);
            ToastBuilder('error', 'Unable to Search Records')
        })
    }

    const displayError = (status, errorMessage) => {
        if (!status) return 'unknown'
        if (!errorMessage) return status
        if (errorMessage && typeof errorMessage === 'object' && Object.keys(errorMessage).length === 0) return status
        return <ModalLinkBuilder onSubmit={false} onCancelLabel={'Close'} label={<span style={{ color: 'red', fontWeight: 'bold' }}>{status}</span>} title='Error Message' body={jsonPre(errorMessage)} size='lg' />
    }

    const displayAction = (action, actionArgs, className = '') => {
        return <ModalLinkBuilder onSubmit={false} onCancelLabel={'Close'} label={<span className={className}>{action}</span>} title='Action Args' body={jsonPre(actionArgs)} size='lg' />
    }

    const displaySecondaryRequest = (request) => {
        let requestText = '';
        for (let i = 0; i < request.length; i++) {
            requestText += `${request[i]}`
            if (i < request.length - 1) {
                requestText += '\n'
            }
        }
        return requestText;
    }

    // Combined Status / Action / Requests cell — stacks the three related signals
    // vertically to reclaim horizontal space while keeping each one's modal/hover
    // behavior. Searching still works via columnsToSearch (status, action,
    // secondary_requests), which reads the underlying row keys, not the column.
    const displayActivity = (row) => {
        const hasRequests = row.secondary_requests && row.secondary_requests.length > 0;
        return (
            <div className='d-flex flex-column align-items-center py-1' style={{ gap: 3, lineHeight: 1.25 }}>
                {/* Status */}
                <div>{displayError(row.status, row.error_message)}</div>
                {/* Action — delete actions flagged red, like error/failed statuses */}
                {row.action ? (
                    <div style={{ fontSize: 12 }}>
                        {(() => {
                            const isDelete = String(row.action).toLowerCase().includes('delete');
                            const actionClass = isDelete ? 'txt-danger' : '';
                            return row.action_args
                                ? displayAction(row.action, row.action_args, actionClass)
                                : <span className={actionClass}>{row.action}</span>;
                        })()}
                    </div>
                ) : null}
                {/* Directive — manual migration-record instruction (e.g. delete).
                    A pending directive shows while set; once carried out the backend
                    clears directive but keeps directive_result, so show the audit trail
                    whenever it exists (independent of directive still being set). */}
                {row.directive_result ? (
                    <div className='txt-danger' style={{ fontSize: 11, fontWeight: 'bold' }}>
                        <ModalLinkBuilder onSubmit={false} onCancelLabel={'Close'}
                            label={<span className='txt-danger'>{`directive: ${row.directive_result[row.directive_result.length - 1]?.directive || 'done'} ✓`}</span>}
                            title='Directive Result' body={jsonPre(row.directive_result)} size='lg' />
                    </div>
                ) : row.directive ? (
                    <div className='txt-danger' style={{ fontSize: 11, fontWeight: 'bold' }}>
                        {`directive: ${row.directive}`}
                    </div>
                ) : null}
                {hasRequests ? (
                    <HoverOver
                        content={displaySecondaryRequest(row.secondary_requests)}
                        contentStyle={{ maxHeight: 600, padding: '15px 45px' }}
                        text={<span className='txt-secondary' style={{ fontSize: 11 }}>{`${row.secondary_requests.length} requests`}</span>}
                        keepHovered={true}
                    />
                ) : null}
            </div>
        );
    }

    // Shared column definition for the merged Status / Action / Requests column.
    // Sorts by status by default. Reused across the per-resource and multi-search tables.
    const activityColumnDef = {
        name: 'Activity',
        selector: (row) => displayActivity(row),
        sortable: true,
        center: true,
        // On mobile use a fixed minWidth so the stacked content isn't squeezed;
        // on desktop keep a proportional width.
        ...(isMobile ? { width: '160px' } : { width: '18%' }),
        sortFunction: (a, b) => sortByKey(a, b, 'status'),
    };

    // Combined Source → Destination record cell. Stacks the source preview above the
    // destination preview with a direction arrow, reclaiming a column while keeping
    // each side's record-data modal and copy button intact.
    // Source → destination side by side, with the arrow pinned to the exact
    // horizontal center. Each side takes equal flex space (source right-aligned,
    // dest left-aligned) so the centered arrow stays centered regardless of each
    // side's content width. Used on both desktop and mobile.
    const displayRecordPair = (row) => (
        <div className='d-flex flex-column w-100 py-1' style={{ gap: 2 }}>
            {/* Row 1: the src → dest pair. A 3-track grid (1fr | auto | 1fr) pins the
                arrow to the exact horizontal center of the cell — the two 1fr tracks
                are always equal regardless of each side's content width, so the arrow
                never drifts. Source hugs the arrow from the left, dest from the right. */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', width: '100%', columnGap: 8 }}>
                <span className='d-flex justify-content-end' style={{ whiteSpace: 'nowrap', overflow: 'visible' }}>
                    {handleRecordPreview(row, 'source')}
                </span>
                <span className='text-center' style={{ fontSize: 13, opacity: 0.5 }}>→</span>
                <span className='d-flex justify-content-start' style={{ whiteSpace: 'nowrap', overflow: 'visible' }}>
                    {handleRecordPreview(row, 'destination')}
                </span>
            </div>
            {/* Row 2 (only when promoted): full-width, centered, below the pair. */}
            {row.override_destination_rec_id ? (
                <div className='text-center'>
                    <span className='txt-secondary' style={{ fontSize: 10, whiteSpace: 'nowrap' }}>
                        destination override: {row.override_resource_name ? `${row.override_resource_name} ` : ''}{row.override_destination_rec_id}
                    </span>
                </div>
            ) : null}
        </div>
    );

    // Shared column definition for the merged Source → Destination record column.
    // Sorts by source rec id. Reused across the per-resource and multi-search tables.
    const recordColumnDef = {
        name: 'Record (src → dest)',
        selector: (row) => displayRecordPair(row),
        sortable: true,
        center: true,
        // react-data-table-component wraps every cell in a div with the default
        // `overflow: hidden`. The arrow stays centered by giving each side flex:1
        // + minWidth:0, but that lets a side shrink below its id and clip. Fix is
        // a generous column width floor (both halves + arrow fit) and letting the
        // cell content show on one line rather than clip.
        style: { overflow: 'visible', whiteSpace: 'nowrap' },
        ...(isMobile ? { minWidth: '260px', grow: 1 } : { minWidth: '340px', grow: 2 }),
        sortFunction: (a, b) => sortByKey(a, b, 'source_rec_id'),
    };

    const handleDMTAction = (action, formData = null, myCallback = null, showNotification = false, resetForm = false) => {

        const sourceResourceId = formData.resourceId

        ApiRequest('POST', '/dmt/jobs/{jobId}/actions/{actionName}', { pathParams: { jobId, actionName: action }, body: { sourceResourceId, formData } })
            .then(function (results) {
                myCallback ? myCallback(results) : null
                showNotification ? ToastBuilder('success', 'Action Taken Successfully') : null
                resetForm ? reset() : null
            })
    }

    const handleUpdateRecordStatus = async (resourceId, sourceRecId, newStatus) => {
        const res = await ApiRequest('POST', '/dmt/jobs/{jobId}/actions/{actionName}', {
            pathParams: { jobId, actionName: 'update-record-status' },
            body: {
                sourceResourceId: resourceId,
                formData: {
                    sourceRecId: parseInt(sourceRecId),
                    newStatus,
                    isSync: !!jobData.is_sync,
                },
            },
        });
        console.log('update-record-status', res);
        if (res.success) {
            ToastBuilder('success', res.data?.message || 'Status updated');
            onRecordSearch(getValues(), true);
        } else {
            ToastBuilder('error', res.error || 'Failed to update status');
        }
    };

    const handleLoadRecordData = async (resourceId, sourceRecId, resourceData, destinationRecId) => {
        const res = await ApiRequest('POST', '/dmt/jobs/{jobId}/actions/{actionName}', {
            pathParams: { jobId, actionName: 'load-record-data' },
            body: {
                sourceResourceId: resourceId,
                formData: {
                    sourceRecId: parseInt(sourceRecId),
                    resourceData: resourceData || null,
                    destinationRecId: destinationRecId || null,
                    isSync: !!jobData.is_sync,
                },
            },
        });
        console.log('load-record-data', res);
        if (res.success) {
            ToastBuilder('success', res.data?.message || 'Record data loaded');
            onRecordSearch(getValues(), true);
        } else {
            ToastBuilder('error', res.error || 'Failed to load record data');
        }
    };

    const handleManageSyncRecord = async (resourceId, sourceRecId, formData) => {
        // Only send fields that were explicitly set — undefined/empty = skip
        const body = { sourceRecId: parseInt(sourceRecId), isSync: !!jobData.is_sync };

        if (formData.manageStatus && formData.manageStatus !== '') body.newStatus = formData.manageStatus;
        if (formData.manageResourceData && formData.manageResourceData.trim() !== '') body.resourceData = formData.manageResourceData;
        if (formData.manageDestRecId && formData.manageDestRecId.trim() !== '') {
            body.destinationRecId = formData.manageDestRecId.trim();
        }
        if (formData.manageClearCache === 'true') body.clearCache = true;

        // Clear activity log is a separate action
        const shouldClearLog = formData.manageClearLog && formData.manageClearLog !== '';

        const res = await ApiRequest('POST', '/dmt/jobs/{jobId}/actions/{actionName}', {
            pathParams: { jobId, actionName: 'manage-sync-record' },
            body: {
                sourceResourceId: resourceId,
                formData: body,
            },
        });
        console.log('manage-sync-record', res);

        // Fire clear-activity-log if selected
        if (shouldClearLog) {
            const logRes = await ApiRequest('POST', '/dmt/jobs/{jobId}/actions/{actionName}', {
                pathParams: { jobId, actionName: 'clear-activity-log' },
                body: {
                    sourceResourceId: resourceId,
                    formData: { sourceRecId: parseInt(sourceRecId), keep: formData.manageClearLog === 'delete-all' ? 0 : parseInt(formData.manageClearLog) },
                },
            });
            console.log('clear-activity-log', logRes);
            if (logRes.success) {
                ToastBuilder('success', logRes.data?.message || 'Activity log cleared');
            } else {
                ToastBuilder('error', logRes.error || 'Failed to clear activity log');
            }
        }

        if (res.success) {
            ToastBuilder('success', res.data?.message || 'Record managed');
            onRecordSearch(getValues(), true);
        } else {
            ToastBuilder('error', res.error || 'Failed to manage record');
        }
    };

    const [cacheKeysModal, setCacheKeysModal] = useState(false);
    const [cacheKeyOptions, setCacheKeyOptions] = useState([]);
    const [selectedCacheKeys, setSelectedCacheKeys] = useState([]);
    const [cacheKeysContext, setCacheKeysContext] = useState(null); // { resourceId, sourceRecId }
    const [jsonViewerSearch, setJsonViewerSearch] = useState(''); // persisted across record modals

    const handleRecordPreview = (row, type='source') => {
        let record_id = row[`${type}_rec_id`];
        if (type === 'destination'){
            record_id = row.destination_rec_id ? row.destination_rec_id : row.destination_resource_rec_id ? row.destination_resource_rec_id : '';
        }
        if (row[`${type}_raw_id`]) {
            record_id = row[`${type}_raw_id`];
        }
        let record_identifier = row[`${type}_rec_identifier`] && String(row[`${type}_rec_identifier`]) !== String(record_id) ? row[`${type}_rec_identifier`] : null

        // Build display ID: prefer raw_id, fall back to numeric source_rec_id
        const displayId = row[`${type}_raw_id`] || row[`${type}_rec_id`] || '';
        const numericId = row[`${type}_rec_id`];
        const modalTitle = displayId && String(displayId) !== String(numericId)
            ? `Record Data — ${displayId} (${numericId})`
            : `Record Data — ${displayId || numericId || 'Unknown'}`;

        let recPreview = <span className='d-inline-flex align-items-center' style={{ gap: 4, whiteSpace: 'nowrap' }}>
            <LabelWithSubtext text={record_id} subtext={record_identifier} align='center' />
            {(record_identifier || record_id) && <Copy size={12} style={{ cursor: 'pointer', opacity: 0.5, flexShrink: 0 }} onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleCopy(record_identifier || record_id); }} />}
        </span>

        const recordData = type === 'source' ? row.resource_data : (row.destination_resource_data || row.response_data)

        if (!recordData) {
            return recPreview;
        }

        return (
            <ModalLinkBuilder
                title={modalTitle}
                titleActions={
                    <div className='d-flex align-items-center' style={{ gap: 8 }}>
                        {jsonViewerSearch.length >= 2 && (
                            <span style={{ fontSize: 11, whiteSpace: 'nowrap', color: (() => {
                                const escaped = jsonViewerSearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                                const matches = JSON.stringify(recordData, null, 2).match(new RegExp(escaped, 'gi'));
                                return matches && matches.length > 0 ? '#198754' : '#dc3545';
                            })() }}>
                                {(() => {
                                    const escaped = jsonViewerSearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                                    const matches = JSON.stringify(recordData, null, 2).match(new RegExp(escaped, 'gi'));
                                    const count = matches ? matches.length : 0;
                                    return `${count} match${count !== 1 ? 'es' : ''}`;
                                })()}
                            </span>
                        )}
                        <input
                            type='text'
                            className='form-control'
                            placeholder='Search...'
                            value={jsonViewerSearch}
                            onChange={(e) => setJsonViewerSearch(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            style={{ width: 180, height: 28, fontSize: 12 }}
                        />
                    </div>
                }
                onSubmit={false}
                onCancelLabel="Close"
                size="xl"
                // On mobile the xl modal's % max-width leaves it too narrow; widen the
                // dialog to 90% of the viewport. reactstrap applies style to .modal-dialog.
                style={isMobile ? { maxWidth: '90%', width: '90%' } : {}}
                body={<JsonViewer data={recordData} search={jsonViewerSearch} onSearchChange={setJsonViewerSearch} />}
                label={recPreview}
            />
        )
    }

    useEffect(() => {
        ApiRequest('GET', '/dmt/settings').then(function (results) {
            if (results.data && results.data.length > 0) {
                setAvailableStatuses(results.data[0].states || []);
                setAvailableActions(results.data[0].actions || []);
            }
            setSettingsLoaded(true);
        })
    }, []);

    useEffect(() => {
        if (settingsLoaded && jobData && (parseInt(jobData.partner_id) === portalEmulation.partnerId || isAdmin)) {
            if (!loadingPage) {
                setLoadingPage(true)
                getTileData()
                if (isAdmin && parseInt(jobData.partner_id) !== portalEmulation.partnerId) {
                    portalEmulation.partnerId !== 0 ? ToastBuilder('success', "Admin View is overriding current partner", { width: 350 }, 6000) : null
                    setTimeout(() => setLoadingPage(false), 750)
                }
            } else {
                console.log('loadingPage')
            }
        } else if (settingsLoaded && (!jobData || parseInt(jobData.partner_id) !== portalEmulation.partnerId)) {
            navigate(`${process.env.PUBLIC_URL}/tools/data-migration`)
        }
    }, [settingsLoaded, jobData]);

    // Apply page parameters passed via the URL query string once the dashboard
    // data is ready. Lets other pages deep-link into a pre-filtered Progress view,
    // e.g. .../progress?resource=sites&status=error&sort=updated_at%20ASC&tab=1
    // or the original .../progress?record_id=123 (back-compat).
    //   record_id → runs a record search for that id
    //   resource   → preselects the resource filter (job_resource_id)
    //   status     → preselects the status filter (record_status)
    //   sort       → preselects the sort order
    //   tab        → activates a result tab by index
    usePageParams(
        {
            record_id: { type: 'string' },
            resource:  { type: 'string' },   // maps to job_resource_id
            status:    { type: 'string' },   // maps to record_status
            sort:      { type: 'string' },   // maps to sort_order
            tab:       { type: 'string' },   // maps to BasicTab (index as string)
        },
        {
            // Wait until the dashboard has loaded its resources and is ready to render results.
            ready: renderResults && Object.values(jobResources).length > 0,
            onReady: (params, anyPresent) => {
                if (!anyPresent) return;

                // resource/status are multiselects now — accept comma-separated URL values.
                if (params.resource !== undefined) setValue('job_resource_id', String(params.resource).split(',').map(s => s.trim()).filter(Boolean));
                if (params.status !== undefined) setValue('record_status', String(params.status).split(',').map(s => s.trim()).filter(Boolean));
                if (params.sort !== undefined) setValue('sort_order', params.sort);
                if (params.record_id !== undefined) setValue('record_id', params.record_id);

                // Any filter/search param should trigger a (silent) search with the seeded values.
                if (['record_id', 'resource', 'status', 'sort'].some(k => params[k] !== undefined)) {
                    onRecordSearch({ ...getValues(), ...(params.record_id !== undefined ? { record_id: params.record_id } : {}) }, true);
                }

                // Activate the requested result tab after the search sets its default ('0').
                if (params.tab !== undefined) setBasicTab(String(params.tab));
            },
        }
    );

    const resourceOptions = useMemo(() =>
        Object.values(jobResources).filter(r => r.filter !== 'All').map(r => ({ label: r.resource, value: r.filter }))
    , [Object.keys(jobResources).join(',')])

    const statusOptions = useMemo(() =>
        (availableStatuses.length > 0 ? availableStatuses.map(s => ({ label: s.name, value: s.name })) : ['staged', 'queued', 'complete', 'error', 'dryRun'].map(s => ({ label: s, value: s })))
    , [availableStatuses])

    const orderByOptions = useMemo(() => [
        { label: 'Updated (Newest)', value: 'updated_at DESC' },
        { label: 'Updated (Oldest)', value: 'updated_at ASC' },
        { label: 'Record ID (High→Low)', value: 'source_rec_id DESC' },
        { label: 'Record ID (Low→High)', value: 'source_rec_id ASC' },
    ], [])

    const manageColumnDef = {
        name: 'Manage',
        sortable: false,
        center: true,
        // Fixed minWidth (not a %) so the Queue button + ☰ toggle are never squeezed
        // below their own size and clipped. Tighter on mobile to free space for the
        // Record column; the table's horizontal scroll still reaches it either way.
        ...(isMobile ? { width: '170px' } : { width: '18%' }),
        omit: !isAdmin,
        selector: (row) => {
            const resourceId = jobResources[row.job_resource_id]?.resource_id;
            return <DMTRecordManage
                row={row}
                resourceId={resourceId}
                jobId={jobId}
                jobData={jobData}
                isMobile={isMobile}
                statusOptions={statusOptions}
                form={{ register, errors, control, setValue, handleSubmit, getValues }}
                handlers={{ handleDMTAction, handleManageSyncRecord, onRecordSearch }}
                cacheModal={{ setCacheKeyOptions, setSelectedCacheKeys, setCacheKeysContext, setCacheKeysModal }}
            />;
        }
    };

    return (
        <Fragment>
            {showLoader ? <ContentLoader isLoading={isProcessing} /> : null}
            <Row>
                <Container fluid={true}>

                    {showTiles ? <TilesBuilder data={tiles} container={false} /> : null}

                    <Row>
                        {showDetails ? <Col xs={12}>
                            <Card>
                                <CardBody style={{ padding: '16px 24px' }}>
                                    {renderJobDetails()}
                                </CardBody>
                            </Card>
                        </Col> : null}

                        <Col xs={12}>
                            <Form id='search-records-form' className='card' onSubmit={handleSubmit(onRecordSearch)}>
                                <CardBody className='input-md' style={{ padding: '20px 24px', pointerEvents: Object.values(jobResources).length > 0 ? 'auto' : 'none' }}>
                                    <h5 className='mb-3'>Search Records</h5>
                                    {showDisclaimer && <p style={{ fontSize: 12, color: '#999', marginBottom: 12 }}>Large dataset — results are limited. Use filters to find specific records.</p>}
                                        <Row>
                                            <RegisteredInputsBuilder
                                                registrator={register}
                                                registratorErrors={errors}
                                                control={control}
                                                inputs={[
                                                    {
                                                        label: 'Resources',
                                                        name: 'job_resource_id',
                                                        type: 'multiselect',
                                                        options: resourceOptions,
                                                        placeholder: 'All resources',
                                                        size: 3,
                                                        settings: { topAligned: true, compact: true }
                                                    },
                                                    {
                                                        label: 'Record IDs',
                                                        name: 'record_id',
                                                        type: 'textarea',
                                                        placeholder: 'Source or Destination',
                                                        size: 4,
                                                        settings: { topAligned: true, compact_: true },
                                                        style: { minHeight: 34, maxHeight: 150, overflow: 'auto', resize: 'none', fieldSizing: 'content' },
                                                        onKeyDown: submitOnEnter,
                                                    },
                                                    {
                                                        label: 'Statuses',
                                                        name: 'record_status',
                                                        type: 'multiselect',
                                                        options: statusOptions,
                                                        placeholder: 'All statuses',
                                                        size: 3,
                                                        settings: { topAligned: true, compact: true }
                                                    },
                                                    {
                                                        label: 'Order By',
                                                        name: 'sort_order',
                                                        type: 'reactselect',
                                                        options: orderByOptions,
                                                        size: 2,
                                                        settings: { topAligned: true, compact_: true, treatNullAsEmpty: true }
                                                    },
                                                ]}
                                            />
                                        </Row>
                                        <div style={{ borderTop: '1px solid #eee', marginTop: 0, paddingTop: 12 }}>
                                            <div className='d-flex align-items-center justify-content-between mb-2'>
                                                <div className='d-flex align-items-center gap-3'>
                                                    <span style={{ cursor: 'pointer', fontSize: 12, fontWeight: 600 }} className='txt-primary' onClick={() => { setShowMultiSearch(!showMultiSearch); if (!showMultiSearch) { setShowJsonSearch(false); } }}>
                                                        {showMultiSearch ? '▾ Multi-Resource Search' : '▸ Multi-Resource Search'}
                                                    </span>
                                                    <span style={{ cursor: 'pointer', fontSize: 12, fontWeight: 600 }} className='txt-primary' onClick={() => { setShowJsonSearch(!showJsonSearch); if (!showJsonSearch) { setShowMultiSearch(false); } }}>
                                                        {showJsonSearch ? '▾ JSON Field Search' : '▸ JSON Field Search'}
                                                    </span>
                                                </div>
                                            </div>
                                            {showMultiSearch && (
                                                <div>
                                                    <p style={{ fontSize: 11, color: '#999', marginBottom: 8 }}>Search specific record IDs per resource. Each row queries independently and results are merged.</p>
                                                    {multiSearchRows.map((row, idx) => (
                                                        <div key={idx} className='d-flex align-items-start gap-2 mb-2'>
                                                            <div style={{ flex: '0 0 280px' }}>
                                                                <Select
                                                                    options={Object.values(jobResources).filter(r => r.filter !== 'All').map(r => ({ label: r.resource, value: String(r.filter) }))}
                                                                    placeholder='Select resource...'
                                                                    menuPortalTarget={document.body}
                                                                    isClearable
                                                                    className='react-select-container'
                                                                    classNamePrefix='react-select'
                                                                    value={row.resource ? Object.values(jobResources).filter(r => String(r.filter) === row.resource).map(r => ({ label: r.resource, value: String(r.filter) }))[0] || null : null}
                                                                    onChange={(opt) => {
                                                                        setMultiSearchRows(prev => prev.map((r, i) => i === idx ? { ...r, resource: opt ? opt.value : '' } : r));
                                                                    }}
                                                                    styles={{
                                                                        control: (base) => ({ ...base, minHeight: 34, fontSize: 12 }),
                                                                        option: (base) => ({ ...base, fontSize: 12, padding: '6px 10px' }),
                                                                        menuList: (base) => ({ ...base, fontSize: 12 }),
                                                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                                                        singleValue: (base) => ({ ...base, fontSize: 12 }),
                                                                        placeholder: (base) => ({ ...base, fontSize: 12 }),
                                                                    }}
                                                                />
                                                            </div>
                                                            <div style={{ flex: '0 0 80px' }}>
                                                                <select
                                                                    className='form-select'
                                                                    value={row.idType || 'both'}
                                                                    onChange={(e) => {
                                                                        setMultiSearchRows(prev => prev.map((r, i) => i === idx ? { ...r, idType: e.target.value } : r));
                                                                    }}
                                                                    style={{ minHeight: 34, fontSize: 11, padding: '4px 6px' }}
                                                                >
                                                                    <option value='both'>Both</option>
                                                                    <option value='source'>Source</option>
                                                                    <option value='dest'>Dest</option>
                                                                </select>
                                                            </div>
                                                            <div style={{ flex: 1 }}>
                                                                <textarea
                                                                    className='form-control'
                                                                    placeholder='Record IDs (comma or newline separated)'
                                                                    value={row.recordIds}
                                                                    onChange={(e) => {
                                                                        setMultiSearchRows(prev => prev.map((r, i) => i === idx ? { ...r, recordIds: e.target.value } : r));
                                                                    }}
                                                                    onKeyDown={submitOnEnter}
                                                                    style={{ minHeight: 34, maxHeight: 100, fontSize: 12, overflow: 'auto', resize: 'none', fieldSizing: 'content' }}
                                                                />
                                                            </div>
                                                            <div className='d-flex gap-1' style={{ paddingTop: 4 }}>
                                                                {multiSearchRows.length > 1 && (
                                                                    <Trash2 size={16} style={{ cursor: 'pointer', color: '#dc3545' }}
                                                                        onClick={() => setMultiSearchRows(prev => prev.filter((_, i) => i !== idx))} />
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                    <div className='d-flex align-items-center gap-2' style={{ marginTop: 4 }}>
                                                        <span style={{ cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }} className='txt-primary'
                                                            onClick={() => setMultiSearchRows(prev => [...prev, { resource: '', recordIds: '', idType: 'both' }])}>
                                                            <Plus size={14} /> Add Row
                                                        </span>
                                                        {multiSearchRows.length > 1 && (
                                                            <span style={{ cursor: 'pointer', fontSize: 12 }} className='txt-secondary'
                                                                onClick={() => setMultiSearchRows([{ resource: '', recordIds: '', idType: 'both' }])}>
                                                                Clear All
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                            {showJsonSearch && (
                                                <DMTJsonFieldSearch
                                                    jobResources={jobResources}
                                                    showMultiSearch={showMultiSearch}
                                                    operators={JSON_SEARCH_OPERATORS}
                                                    columnIsJson={columnIsJson}
                                                    opIsNoValue={opIsNoValue}
                                                    opIsList={opIsList}
                                                    columnOptions={jsonColumnOptions}
                                                    filterRows={jsonFilterRows}
                                                    setFilterRows={setJsonFilterRows}
                                                    match={jsonMatch}
                                                    setMatch={setJsonMatch}
                                                    mode={jsonSearchMode}
                                                    setMode={setJsonSearchMode}
                                                    resource={jsonSearchResource}
                                                    setResource={setJsonSearchResource}
                                                    groupBy={jsonGroupBy}
                                                    setGroupBy={setJsonGroupBy}
                                                    countResult={jsonCountResult}
                                                    isResult={isJsonSearchResult}
                                                    emptyFilter={emptyJsonFilter}
                                                />
                                            )}
                                        </div>
                                    <div className='d-flex justify-content-end gap-2 mt-0'>
                                        <ButtonBuilder form='' label='Reset' className='btn-secondary' style={{ fontSize: 12 }} onClick={() => { reset(); setMultiSearchRows([{ resource: '', recordIds: '', idType: 'both' }]); setJsonFilterRows([emptyJsonFilter()]); setJsonMatch('and'); setJsonSearchMode('list'); setJsonSearchResource(''); setJsonGroupBy({ column: '', path: '' }); setJsonCountResult(null); setIsJsonSearchResult(false); }} disabled={Object.values(jobResources).length === 0} />
                                        <ButtonBuilder label='Search' className='btn-primary' style={{ fontSize: 12 }} disabled={Object.values(jobResources).length === 0} />
                                    </div>
                                </CardBody>
                            </Form></Col>

                    </Row>
                    <Col xl="12">
                        <Card>
                            <CardBody>
                                {Object.values(jobResources).length > 0 ? (
                                <>
                                {(isMultiSearchResult || (isJsonSearchResult && !jsonCountResult)) ? (
                                    <TableBuilder
                                        subHeader={true}
                                        styles={{ cells: { style: { overflow: 'visible' } } }}
                                        columnsToSearch={['name', 'source_rec_id', 'destination_resource_rec_id', 'override_destination_rec_id', 'status', 'action', 'directive', 'secondary_requests', 'source_raw_id', 'destination_raw_id', 'source_rec_identifier', 'destination_rec_identifier', 'updated_at_display']}
                                        title={isJsonSearchResult ? 'JSON Field Search Results' : 'Multi-Resource Search Results'}
                                        data={displayRecords || []}
                                        sortField={null}
                                        processing={!renderResults}
                                        columns={[
                                            {
                                                name: 'Resource',
                                                selector: (row) => jobResources[row.job_resource_id]?.title || row.job_resource_id,
                                                sortable: true,
                                                center: true,
                                                width: '13%',
                                            },
                                            recordColumnDef,
                                            activityColumnDef,
                                            {
                                                name: 'Updated',
                                                selector: (row) => isMobile ? showTimestampStacked(row.updated_at * 1000, { align: 'center' }) : showTimestamp(row.updated_at * 1000),
                                                sortable: true,
                                                center: true,
                                                ...(isMobile ? { width: '150px' } : { width: '18%' }),
                                                sortFunction: (a, b) => a.updated_at - b.updated_at,
                                            },
                                            manageColumnDef,
                                        ]}
                                    />
                                ) : (
                                <>
                                <Nav tabs>
                                    {/* {jobResources.filter(tab => tab.title !== 'none').map((tab, i) => */}
                                    {Object.values(jobResources).filter(tab => tab.title !== 'none').sort((a, b) => b.fetch_count - a.fetch_count).map((tab, i) =>
                                        <NavItem key={tab.title}>
                                            <NavLink href="#" className={BasicTab === String(i) ? 'active' : ''} onClick={() => { setBasicTab(String(i)) }}>{tab.title}</NavLink>
                                        </NavItem>
                                    )}
                                </Nav>


                                <TabContent activeTab={BasicTab}>
                                    {Object.values(jobResources).filter(tab => tab.title !== 'none').sort((a, b) => b.fetch_count - a.fetch_count).map((tab, i) =>
                                        <TabPane className="fade show" tabId={String(i)} key={tab.title + i}>
                                            <TableBuilder
                                                subHeader={true}
                                                styles={{ cells: { style: { overflow: 'visible' } } }}
                                                columnsToSearch={['name', 'source_rec_id', 'destination_resource_rec_id', 'override_destination_rec_id', 'status', 'action', 'directive', 'secondary_requests', 'source_raw_id', 'destination_raw_id', 'source_rec_identifier', 'destination_rec_identifier', 'updated_at_display']}
                                                title='Search Results'
                                                data={displayRecords && displayRecords.length > 0 ? displayRecords.filter(record => record.job_resource_id === tab.filter) : []}
                                                sortField={null}
                                                processing={!renderResults}
                                                columns={[
                                                    recordColumnDef,
                                                    activityColumnDef,
                                                    {
                                                        name: 'Updated',
                                                        selector: (row) => isMobile ? showTimestampStacked(row.updated_at * 1000, { align: 'center' }) : showTimestamp(row.updated_at * 1000),
                                                        sortable: true,
                                                        center: true,
                                                        ...(isMobile ? { width: '150px' } : { width: '18%' }),
                                                        sortFunction: (a, b) => {
                                                            return a.updated_at - b.updated_at
                                                        },
                                                    },
                                                    manageColumnDef,
                                                ]}
                                            />
                                        </TabPane>
                                    )}
                                </TabContent>
                                </>
                                )}
                                </>
                                ) : (
                                    !isProcessing && (
                                        <div className='text-center py-5'>
                                            <p style={{ fontSize: 16, color: '#999', marginBottom: 8 }}>No records found for this job</p>
                                            <p style={{ fontSize: 13, color: '#bbb' }}>Records will appear here once the job has been run.</p>
                                        </div>
                                    )
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Container>
            </Row>

            <div style={{ height: 80 }} />

            {/* Clear Cache Modal */}
            <CommonModal
                isOpen={cacheKeysModal}
                toggler={() => setCacheKeysModal(false)}
                title='Clear State Cache'
                size='md'
                onSubmit={false}
                onCancelLabel='Close'
            >
                <p style={{ fontSize: 12, marginBottom: 16 }}>Clear all cached state data, or select specific keys to remove.</p>

                <div style={{ marginBottom: 16 }}>
                    <ButtonBuilder form='' label='Clear All Cache' className='btn-outline-danger btn-sm' style={{ fontSize: 12 }}
                        onClick={async () => {
                            const res = await ApiRequest('POST', '/dmt/jobs/{jobId}/actions/{actionName}', {
                                pathParams: { jobId, actionName: 'clear-state-cache' },
                                body: {
                                    sourceResourceId: cacheKeysContext?.resourceId,
                                    formData: { sourceRecId: parseInt(cacheKeysContext?.sourceRecId), mode: 'all' },
                                },
                            });
                            if (res.success) {
                                ToastBuilder('success', res.data?.message || 'Cache cleared');
                                setCacheKeysModal(false);
                            } else {
                                ToastBuilder('error', res.error || 'Failed to clear cache');
                            }
                        }} useSpinner={false} />
                </div>

                <hr style={{ margin: '12px 0' }} />

                <p style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Or clear specific keys:</p>
                <Select
                    isMulti
                    className='react-select-container'
                    classNamePrefix='react-select'
                    options={cacheKeyOptions}
                    value={selectedCacheKeys}
                    onChange={(val) => setSelectedCacheKeys(val || [])}
                    placeholder='Select keys to clear...'
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                />
                {selectedCacheKeys.length > 0 && (
                    <div style={{ marginTop: 10 }}>
                        <ButtonBuilder form='' label={`Clear ${selectedCacheKeys.length} Key(s)`} className='btn-outline-danger btn-sm' style={{ fontSize: 12 }}
                            onClick={async () => {
                                const keysToRemove = selectedCacheKeys.map(k => k.value);
                                const clearRes = await ApiRequest('POST', '/dmt/jobs/{jobId}/actions/{actionName}', {
                                    pathParams: { jobId, actionName: 'clear-state-cache' },
                                    body: {
                                        sourceResourceId: cacheKeysContext?.resourceId,
                                        formData: { sourceRecId: parseInt(cacheKeysContext?.sourceRecId), mode: 'keys', keys: keysToRemove },
                                    },
                                });
                                if (clearRes.success) {
                                    ToastBuilder('success', clearRes.data?.message || `Cleared ${keysToRemove.length} key(s)`);
                                    setCacheKeysModal(false);
                                } else {
                                    ToastBuilder('error', clearRes.error || 'Failed to clear keys');
                                }
                            }} useSpinner={false} />
                    </div>
                )}
            </CommonModal>
        </Fragment>
    );
};

export default DMTProgressDashboard;
